import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MailIcon from '@/public/icons/approval/mail.svg';
import CheckIcon from '@/public/icons/approval/check.svg';
import { normalizeLang } from '@/utils/approval/i18nText';
import { pickLang } from './statusMapping';
import {
  ReadersAddButton,
  ReadersAddRow,
  ReadersHeading,
  ReadersHeadingCheck,
  ReadersInlineCheck,
  ReadersRoot,
  ReadersTable,
  ReadersTableCount,
  ReadersTableLabel,
  ReadersTableMembers,
  ReadersTableRow,
} from './styles';
import type {
  ApprovalViewApprover,
  ApprovalViewReader,
  ApprovalViewReferrer,
} from './types';

/**
 * 이 문서를 읽은사람들 영역 — 결재/협조/참조 row + 추가 참조인 row 표. readers 매칭 시 이름 좌측 ✓.
 */

type ReadersSlotProps = {
  /** 결재자 (결재 + 협조결재 type=2 모두 포함) */
  approvers: ApprovalViewApprover[];
  /** 참조인 (작성 시 지정) */
  referrers: ApprovalViewReferrer[];
  /** 추가 참조인 (사후 추가) */
  additionalReferrers: ApprovalViewReferrer[];
  /** 읽은 사람들 (read 표시용 passportId set 으로 사용) */
  readers: ApprovalViewReader[];
  /** "참조인 추가" 버튼 노출 여부 — 레거시 조건은 부모가 판정해 전달. 미전달 시 미노출. */
  canAddReferrer?: boolean;
  /** "참조인 추가" 클릭 시 참조인 추가 모달을 여는 핸들러. */
  onAddReferrer?: () => void;
};

type Person = {
  passportId: string;
  intranetId: string;
  displayName: string;
};

const ReadersSlot = ({
  approvers,
  referrers,
  additionalReferrers,
  readers,
  canAddReferrer = false,
  onAddReferrer,
}: ReadersSlotProps) => {
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  // 읽음 set — 응답이 한쪽만 채워진 케이스 대비 passportId/intranetId 둘 다 보관.
  const readSets = useMemo(() => {
    const passportIds = new Set<string>();
    const intranetIds = new Set<string>();
    readers.forEach((r) => {
      if (r.passportId) passportIds.add(r.passportId);
      if (r.intranetId) intranetIds.add(r.intranetId);
    });
    return { passportIds, intranetIds };
  }, [readers]);

  const isRead = (p: { passportId?: string; intranetId?: string }) =>
    (!!p.passportId && readSets.passportIds.has(p.passportId)) ||
    (!!p.intranetId && readSets.intranetIds.has(p.intranetId));

  /** 결재/협조/참조 합쳐서 노출 */
  const mainGroup = useMemo<Person[]>(() => {
    const items: Person[] = [];
    approvers.forEach((a) => {
      const name = pickLang(a.name, lang);
      items.push({
        passportId: a.passportId,
        intranetId: a.intranetId,
        displayName: name,
      });
    });
    referrers.forEach((r) => {
      const name = pickLang(r.name, lang);
      items.push({
        passportId: r.passportId,
        intranetId: r.intranetId,
        displayName: name,
      });
    });
    return items;
  }, [approvers, referrers, lang]);

  const additionalGroup = useMemo<Person[]>(
    () =>
      additionalReferrers.map((r) => ({
        passportId: r.passportId,
        intranetId: r.intranetId,
        displayName: pickLang(r.name, lang),
      })),
    [additionalReferrers, lang],
  );

  const renderNames = (people: Person[]) => {
    if (people.length === 0) return '-';
    return people.map((p, idx) => {
      const read = isRead(p);
      return (
        <span key={`${p.passportId || p.intranetId}-${idx}`}>
          {idx > 0 && ', '}
          {read && (
            <ReadersInlineCheck aria-label={t('읽음')}>
              <CheckIcon width={12} height={12} />
            </ReadersInlineCheck>
          )}
          {p.displayName}
          {p.intranetId ? `(${p.intranetId})` : ''}
        </span>
      );
    });
  };

  const handleAdd = () => {
    onAddReferrer?.();
  };

  return (
    <ReadersRoot>
      <ReadersHeading>
        {t('이 문서를 읽은사람들')} ({t('읽음')}:
        <ReadersHeadingCheck aria-hidden>
          <CheckIcon width={12} height={12} />
        </ReadersHeadingCheck>
        )
      </ReadersHeading>
      <ReadersTable>
        <ReadersTableRow>
          <ReadersTableLabel>{t('결재, 협조, 참조인')} :</ReadersTableLabel>
          <ReadersTableMembers>{renderNames(mainGroup)}</ReadersTableMembers>
          <ReadersTableCount>
            {t('{{count}}명', { count: mainGroup.length })}
          </ReadersTableCount>
        </ReadersTableRow>
        <ReadersTableRow>
          <ReadersTableLabel>{t('추가 참조인')} :</ReadersTableLabel>
          <ReadersTableMembers>
            {renderNames(additionalGroup)}
          </ReadersTableMembers>
          <ReadersTableCount>
            {t('{{count}}명', { count: additionalGroup.length })}
          </ReadersTableCount>
        </ReadersTableRow>
      </ReadersTable>
      {canAddReferrer && (
        <ReadersAddRow>
          <ReadersAddButton
            type="button"
            onClick={handleAdd}
            aria-label={t('참조인 추가')}
          >
            <MailIcon width={12} height={12} />
            {t('참조인 추가')}
          </ReadersAddButton>
        </ReadersAddRow>
      )}
    </ReadersRoot>
  );
};

export default ReadersSlot;
